import { ONBOARDING_DOCUMENT_TYPE, type OnboardingDocument } from "./types";
import {
  isAcceptedFile,
  sizeLimitFor,
  type UploadFileTypes,
} from "./upload-file-types";

export const FILE_KIND = {
  image: "image",
  table: "table",
  document: "document",
  code: "code",
  archive: "archive",
  file: "file",
} as const;

type FileKind = (typeof FILE_KIND)[keyof typeof FILE_KIND];

interface UploadFileSelection {
  accepted: File[];
  oversized: File[];
  excess: File[];
  /** Dropped files of a type this pane does not take. A drop ignores `accept`. */
  wrongType: File[];
}

const CONTENT_TYPE_KIND: Record<string, FileKind> = {
  "application/pdf": FILE_KIND.document,
  "application/msword": FILE_KIND.document,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    FILE_KIND.document,
  "application/rtf": FILE_KIND.document,
  "text/csv": FILE_KIND.table,
  "application/vnd.ms-excel": FILE_KIND.table,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    FILE_KIND.table,
  "application/vnd.oasis.opendocument.spreadsheet": FILE_KIND.table,
  "application/json": FILE_KIND.code,
  "application/xml": FILE_KIND.code,
  "text/xml": FILE_KIND.code,
  "application/zip": FILE_KIND.archive,
  "application/x-zip-compressed": FILE_KIND.archive,
};

const EXTENSION_KIND: Record<string, FileKind> = {
  png: FILE_KIND.image,
  jpg: FILE_KIND.image,
  jpeg: FILE_KIND.image,
  heic: FILE_KIND.image,
  webp: FILE_KIND.image,
  gif: FILE_KIND.image,
  pdf: FILE_KIND.document,
  doc: FILE_KIND.document,
  docx: FILE_KIND.document,
  txt: FILE_KIND.document,
  rtf: FILE_KIND.document,
  csv: FILE_KIND.table,
  tsv: FILE_KIND.table,
  xls: FILE_KIND.table,
  xlsx: FILE_KIND.table,
  numbers: FILE_KIND.table,
  json: FILE_KIND.code,
  xml: FILE_KIND.code,
  zip: FILE_KIND.archive,
  gz: FILE_KIND.archive,
  rar: FILE_KIND.archive,
  "7z": FILE_KIND.archive,
};

/**
 * The extension wins when it is known. The content type is the browser's guess, and it
 * can be wrong. The content type is the fallback for an unknown extension.
 */
export const fileKindFor = (
  contentType: string | null | undefined,
  fileName: string | null | undefined,
): FileKind => {
  const extension = fileName?.toLowerCase().split(".").pop() ?? "";
  if (Object.hasOwn(EXTENSION_KIND, extension)) {
    return EXTENSION_KIND[extension];
  }

  const type = contentType?.toLowerCase() ?? "";
  if (type.startsWith("image/")) return FILE_KIND.image;
  if (type.startsWith("text/plain")) return FILE_KIND.document;

  return Object.hasOwn(CONTENT_TYPE_KIND, type)
    ? CONTENT_TYPE_KIND[type]
    : FILE_KIND.file;
};

export const formatFileSize = (bytes?: number | null) => {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const selectUploadFiles = (
  files: File[],
  maxFiles: number,
  maxSize: number,
  types?: UploadFileTypes,
): UploadFileSelection => {
  const wrongType = types
    ? files.filter((file) => !isAcceptedFile(file, types))
    : [];
  const right = files.filter((file) => !wrongType.includes(file));
  // A zip carries its own, larger limit, so the ceiling is per file rather than one
  // number for the pane.
  const limit = (file: File) => (types ? sizeLimitFor(file, types) : maxSize);
  const oversized = right.filter((file) => file.size > limit(file));
  const eligible = right.filter((file) => file.size <= limit(file));

  return {
    accepted: eligible.slice(0, maxFiles),
    oversized,
    excess: eligible.slice(maxFiles),
    wrongType,
  };
};

export const groupOnboardingDocuments = (
  documents: OnboardingDocument[] | undefined,
) => {
  const payPlans: OnboardingDocument[] = [];
  const referenceData: OnboardingDocument[] = [];
  const untyped: OnboardingDocument[] = [];

  documents?.forEach((document) => {
    if (document.document_type === ONBOARDING_DOCUMENT_TYPE.payPlan) {
      payPlans.push(document);
    } else if (
      document.document_type === ONBOARDING_DOCUMENT_TYPE.referenceData
    ) {
      referenceData.push(document);
    } else {
      untyped.push(document);
    }
  });

  return { payPlans, referenceData, untyped };
};
